"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CheckerInput,
  ToneLegend,
} from "@/components/tools/style-checkers-a/a-frame";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  checkNumberStyle,
  type NumberIssue,
  type NumberIssueKind,
  type NumberStyle,
} from "@/lib/tool-kit/style-checkers/number-style";

/** English strings. A translated content file overrides these through `ui`. */
export const NUMBER_STYLE_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a results section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "5 students left early. We recruited 5 participants and twelve teachers from 3 schools. The test lasted five minutes, and 25% of students scored above the mean (n = 40). Agreement was five percent in Table 3. The 21st century began on January 1, 2001. Over two million people took part in the 3rd round.",
  styleLabel: "Check against",
  styleApa: "APA 7",
  styleChicago: "Chicago 18",
  ignoreQuotes: "Ignore quoted text",
  enterPrompt:
    "Paste or type some text to check which numbers should be words and which should be numerals.",
  results: "Results",
  numbersFound: "Numbers found",
  toFix: "To check",
  numerals: "As numerals",
  inWords: "In words",
  words: "Words",
  sentences: "Sentences",
  judgement:
    "Rules, not taste: but the checker reads patterns, not meaning. A score, a statistic or a label can look like a count, so each flag says which rule applies and you decide.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with number-style issues highlighted",
  hoverHint:
    "Hover over a highlight to see the rule; the table below gives the replacement for each.",
  noneFound: "No numbers found.",
  allFine: "Every number follows the rules for this style.",
  findingsHeading: "What to change",
  colWritten: "As written",
  colRule: "Rule",
  colReplace: "Replace with",
  colSentence: "In your sentence",
  legendSpellOut: "Write as words",
  legendUseNumeral: "Write as numerals",
  legendSentenceStart: "Starts a sentence",
  legendPercent: "Percent",
  legendUnit: "Number with a unit",
  legendOrdinal: "Ordinal",
  reword: "Reword the sentence so it does not open with a number",
  ruleSpellOutApa:
    "APA 7 spells out zero through nine in running text. Numerals stay for measurements, statistics, times, dates, ages, scores and numbers after a noun (“Table 3”).",
  ruleSpellOutChicago:
    "Chicago spells out whole numbers from zero through one hundred in non-scientific text. Numerals stay for dates, page numbers, abbreviated units and numbers after a noun.",
  ruleUseNumeralApa: "APA 7 uses numerals for 10 and above.",
  ruleUseNumeralChicago:
    "Chicago uses numerals above one hundred, except round numbers such as two hundred, fifty thousand and 3 million.",
  ruleSentenceStart:
    "A number that opens a sentence is spelled out in both manuals, or the sentence is reworded. A year or a decimal is best moved off the front.",
  rulePercentApa: "APA 7 writes a percentage as a numeral with the % symbol.",
  rulePercentChicago:
    "In non-scientific text Chicago writes a numeral and the word “percent”, not the % symbol.",
  ruleUnitApa:
    "APA 7 uses numerals before a unit of measurement or time, and for exact sums of money.",
  ruleUnitChicago: "Chicago uses numerals with an abbreviated unit (5 km).",
  ruleOrdinalApa:
    "APA 7 spells out ordinals from first through ninth and uses numerals from 10th.",
  ruleOrdinalChicago:
    "Chicago spells out ordinals up to one hundredth (the twenty-first century).",
} as const;

type Strings = typeof NUMBER_STYLE_CHECKER_STRINGS;

const TONES: Record<NumberIssueKind, HighlightTone> = {
  "spell-out": "sky",
  "use-numeral": "amber",
  "sentence-start": "rose",
  percent: "violet",
  unit: "emerald",
  ordinal: "sky",
};

const RULES: Record<NumberIssueKind, readonly [keyof Strings, keyof Strings]> =
  {
    "spell-out": ["ruleSpellOutApa", "ruleSpellOutChicago"],
    "use-numeral": ["ruleUseNumeralApa", "ruleUseNumeralChicago"],
    "sentence-start": ["ruleSentenceStart", "ruleSentenceStart"],
    percent: ["rulePercentApa", "rulePercentChicago"],
    unit: ["ruleUnitApa", "ruleUnitChicago"],
    ordinal: ["ruleOrdinalApa", "ruleOrdinalChicago"],
  };

function ruleText(
  issue: NumberIssue,
  style: NumberStyle,
  strings: Strings
): string {
  const [apa, chicago] = RULES[issue.kind];
  return strings[style === "apa" ? apa : chicago];
}

function replacement(issue: NumberIssue, strings: Strings): string {
  return issue.suggestion ?? strings.reword;
}

export default function NumberStyleCheckerWidget() {
  const strings = useToolUi(NUMBER_STYLE_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [style, setStyle] = useState<NumberStyle>("apa");
  const [ignoreQuotes, setIgnoreQuotes] = useState(true);
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => checkNumberStyle(deferred, { style, ignoreQuotes }),
    [deferred, style, ignoreQuotes]
  );
  const highlights: TextMatch[] = report.issues.map((issue) => ({
    start: issue.start,
    end: issue.end,
    category: issue.kind,
    title: `${ruleText(issue, style, strings)} → ${replacement(issue, strings)}`,
  }));
  const hasText = deferred.trim().length > 0;
  const found = report.numerals + report.inWords;

  return (
    <div className="space-y-6">
      <CheckerInput
        clearLabel={strings.clear}
        example={strings.exampleText}
        exampleLabel={strings.tryExample}
        extra={
          <Checkbox
            checked={ignoreQuotes}
            label={strings.ignoreQuotes}
            onChange={setIgnoreQuotes}
          />
        }
        hint={strings.privacy}
        id="nsc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        value={text}
      />
      <PillGroup
        label={strings.styleLabel}
        name="nsc-style"
        onChange={setStyle}
        options={[
          { value: "apa" as NumberStyle, label: strings.styleApa },
          { value: "chicago" as NumberStyle, label: strings.styleChicago },
        ]}
        value={style}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.toFix}
                value={fmt(report.issues.length)}
              />
              <StatCard label={strings.numbersFound} value={fmt(found)} />
              <StatCard label={strings.numerals} value={fmt(report.numerals)} />
              <StatCard label={strings.inWords} value={fmt(report.inWords)} />
              <StatCard label={strings.words} value={fmt(report.words)} />
              <StatCard
                label={strings.sentences}
                value={fmt(report.sentences)}
              />
            </StatGrid>
            <p className={HINT}>{strings.judgement}</p>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <ToneLegend
              items={[
                { tone: TONES["spell-out"], label: strings.legendSpellOut },
                { tone: TONES["use-numeral"], label: strings.legendUseNumeral },
                {
                  tone: TONES["sentence-start"],
                  label: strings.legendSentenceStart,
                },
                { tone: TONES.percent, label: strings.legendPercent },
                { tone: TONES.unit, label: strings.legendUnit },
              ]}
            />
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {found === 0 ? <Notice>{strings.noneFound}</Notice> : null}
          {found > 0 && report.issues.length === 0 ? (
            <Notice>{strings.allFine}</Notice>
          ) : null}

          {report.issues.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.findingsHeading}</h3>
              <DataTable
                head={[
                  strings.colWritten,
                  strings.colRule,
                  strings.colReplace,
                  strings.colSentence,
                ]}
                rows={report.issues.map((issue) => [
                  issue.text,
                  ruleText(issue, style, strings),
                  replacement(issue, strings),
                  issue.sentence,
                ])}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
